import React, { FormEvent, useEffect, useRef, useState, CSSProperties } from "react";
import { Helmet } from "react-helmet";
import { ROUTES } from "../../lib/constants";
import { CommunityHero } from "../../components/CommunityHero/CommunityHero";
import APIService from '../../lib/api.service';

import superFooterSemicircle from "../../images/community-footer-semicircle-orange.svg";
import { Button } from "../../components/Button/Button";
import { IconsBlocks } from "../../components/IconBlocks/IconBlocks";
import { PulseLoader } from 'react-spinners';

/* import { Video } from "../../components/Video/Video"; */
/* import videoPoster from "../../images/HOA-video-poster.png"; */

import formImageD from "../../images/assc-wifi-d.jpg";
import formImageM from "../../images/assc-wifi-m.jpg";
import { Image } from "../../components/Image/Image";
import imageTextImage from "../../images/hoa-projects-image.jpg";

export const HOAProjects = (): React.JSX.Element => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    street: '',
    state: 'CA',
    city: '',
    businessname: '',
  });

  const [showForm, setShowForm] = useState(true);
  const [showSuccessPrompt, setShowSuccessPrompt] = useState(false);
  const [showFailedPrompt, setShowFailedPrompt] = useState(false);

  // Loading spinner
  const [showLoader, setShowLoader] = useState(false);
  const [color, setColor] = useState("#FFA500");
  const override: CSSProperties = {
    display: "block",
    margin: "0 auto",
    borderColor: "orange",
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setShowLoader(true); // Set spinner
    setShowForm(false);
    setShowSuccessPrompt(false);
    setShowFailedPrompt(false);

    formData["datestamp"] = new Date().toJSON().slice(0, 10); // "YYYY-MM-DD"
    formData["address"] = `${formData["street"]}, ${formData["city"]}, ${formData["state"]}`;
    formData["UTYPE"] = "M";
    formData["phone"] = formData["phone"].replaceAll("-", "").trim() // Sanitize to "XXXXXXXXXX"

    formData["vserviceloc"] = null; // Pulled from previous form
    formData["caccount"] = null;
    formData["campaign"] = null;
    formData["servicelevel"] = null;
    formData["ucount"] = null;
    formData["serviceable"] = "No";

    try {
      const submit = await APIService.postContactInfo(JSON.stringify(formData));

      setShowLoader(false);
      if (submit.status === 201) {
        setShowSuccessPrompt(true);
      } else if (submit.status === 500) {
        setShowFailedPrompt(true);
        setShowForm(true);
      }

    } catch (error) {
      setShowLoader(false);
      setShowFailedPrompt(true);
      setShowForm(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>{ROUTES.hoaProjects.title}</title>
        <meta
          name="description"
          content="Cityside Fiber is the premier choice for high-speed internet and WiFi connectivity for HOA residents. Submit your information today!"
        />

        <meta property="og:title" content={`${ROUTES.hoaProjects.title}`} />
        <meta property="og:description" content="Cityside Fiber is the premier choice for high-speed internet and WiFi connectivity for HOA residents. Submit your information today!" />
        <meta property="og:url" content="https://www.citysidefiber.com/hoa-projects" />
      </Helmet>

      <CommunityHero type="HOA" />

      <section className="grid-12 community-page community-page--hoa">
        <div className="image-text">
          <div className="image-text__image">
            <img src={imageTextImage} alt="" />
          </div>
          <div className="image-text__text">
            <h2>Do you live in an HOA?</h2>
            <p>
              Contact us today, and let&apos;s get your community connected! First,
              we need the HOA to grant Cityside Fiber access by signing a Right
              of Entry Agreement. Then, we will coordinate the planning,
              construction, and installation of the new network.
            </p>

            <p>
              Cityside Fiber can also develop a seamless private network with a
              unique WiFi signal for residents to access throughout common
              areas, pool areas, and parking areas. We can also connect security
              cameras, gates, and more!
            </p>

            <p>
              Our team looks forward to meeting with the HOA Board to discuss
              your options.
            </p>
            <Button
              href="#form-section"
              target="_self"
              size="lg"
              gradient="orange"
            >
              Submit My HOA Info
            </Button>
          </div>
        </div>
        <div className="wifi-solutions">
          <div className="wifi-solutions__text centered">
            <div>
              <h2>Secure WiFi Solutions For HOA Residents and Common Areas</h2>
            </div>
          </div>
          <div className="wifi-solutions__icon-cards">
            <IconsBlocks
              data={[
                {
                  icon: "wifi",
                  iconColor: "marina-blue",
                  borderColor: "marina-blue",
                  text: "Public WiFi",
                },
                {
                  icon: "piggy-bank",
                  iconColor: "citron-yellow",
                  borderColor: "citron-yellow",
                  text: "Increased property value",
                },
                {
                  icon: "security-camera",
                  iconColor: "tangerine-orange",
                  borderColor: "tangerine-orange",
                  text: "Safety and security",
                },
                {
                  icon: "network",
                  iconColor: "marina-blue",
                  borderColor: "marina-blue",
                  text: "Dedicated Network",
                },
                {
                  icon: "mobile-devices",
                  iconColor: "daylight-blue",
                  borderColor: "daylight-blue",
                  text: "Managed, Consistent WiFi",
                },
              ]}
            />
          </div>
          {/* <div className="video-section">
            <Video
              src="https://www.youtube.com/embed/4d6Wc1ys5SU?si=yaskOAHmTW5SY09M"
              poster={videoPoster}
              iconColor="white"
            />
          </div> */}
        </div>
      </section>
      <section className="community-page__form-section ">
        <div id="form-section" className="form-section-scrollto"></div>
        <div className="inner">
          <div className="image-side">
            <Image
              sources={[
                { src: formImageM, breakpoint: 'default' },
                { src: formImageD, breakpoint: '720px' }
              ]}
              alt="Suburban neighborhood"
            />
            {/* <img src={formImage} alt="" /> */}
            <div className="overlay"></div>
            <div className="text">
              <h3 className="headline__h3">
                Smart community solutions are available for homeowner&apos;s associations.
              </h3>
            </div>
          </div>
          <div className="form-side">

            {showLoader &&
              <div className="loader-container">
                <PulseLoader
                  color={color}
                  loading={showLoader}
                  cssOverride={override}
                  size={50}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                />
              </div>
            }

            {showSuccessPrompt &&
              <div className="loader-container">
              <h3 className="headline__h3 color--marina-blue">
                Your community interested has been submitted! If you have any questions, please contact us at <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com.</a>
              </h3>
              </div>
            }

            {showForm &&
            <div>
            <form id="form" ref={formRef} onSubmit={onSubmit}>
              <h3 className="headline__h3 color--marina-blue">
                Submit your community interest
              </h3>

              {showFailedPrompt &&
              <div>
                <p>
                  Hmm... it looks like we're having trouble submitting your interest. Try again in a couple minutes, and if that doesn't work, please contact us at <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com.</a>
                </p>
              </div>
             }

              <div className="multi-column-container">
                <div className="input-container">
                  <label htmlFor="firstname">First Name</label>
                  <input
                    name="firstname"
                    type="text"
                    value={formData.firstname}
                    onChange={handleChange}
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="lastname">Last Name</label>
                  <input
                    name="lastname"
                    type="text"
                    value={formData.lastname}
                    onChange={handleChange}
                  />
                </div>

                <div className="input-container">
                  <label htmlFor="email">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="input-container">
                <label htmlFor="street">Street Address</label>
                <input
                  name="street"
                  type="text"
                  autoComplete="off"
                  placeholder="Steet Address"
                  value={formData.street}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="multi-column-container">
                <div className="input-container">
                  <label htmlFor="city">City</label>
                  <input
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>

                <div className="select-container">
                  <label htmlFor="state">State</label>
                  <select id="state" name="state" defaultValue="CA" required>
                    <option value="CA">California</option>
                  </select>
                </div>
              </div>
              <div className="multi-column-container">
                <div className="input-container">
                  <label htmlFor="phone">Phone</label>
                  <input
                    name="phone"
                    type="tel"
                    pattern='[0-9]{3}-?[0-9]{3}-?[0-9]{4}'
                    placeholder='XXX-XXX-XXXX'
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="input-container"></div>
              </div>
              <div className="multi-column-container">
                <div className="input-container">
                  <label htmlFor="businessname">Community Name</label>
                  <input
                    name="businessname"
                    type="text"
                    value={formData.businessname}
                    onChange={handleChange}
                  />
                </div>
                <div className="input-container"></div>
              </div>
              <Button
                type="submit"
                className="btn--lg-extra-padding"
                size="md-to-lg"
                fullWidth={true}
              >
                Submit
              </Button>
            </form>
            </div>
            }
          </div>
        </div>
      </section>
      <section className="community-page__super-footer hoa">
        <div className="inner">
          <h2>
            A fiber connection can increase your property&apos;s value by 3.1%*
          </h2>
          <div className="citation">
            *bbcmag.com/ftth-council-fiber-delivered-internet-boost-home-values/
          </div>
        </div>
        <img className="semicircle" src={superFooterSemicircle} alt="" />
      </section>
    </>
  );
};
